import React, { useMemo } from 'react';
import KSButton from 'presentation/components/KSButton';
import AuditAlert from './AuditAlert';
import './TransactionsTable.scss';
import { IconFlag, IconFlagFilled } from 'components/Icons';
import { useTransactionsTableContext } from '../Chart/context/TransactionsTableContext';
import { Tooltip, Whisper } from 'rsuite';
import { getFlaggingTooltipMessage } from './utils';
import classNames from 'classnames';
import './TransactionsTable.scss';
import { useNTContext } from 'presentation/components/NewTable/NTContext';

type Props = {
  cellLabel?: string;
  selectedTransaction?: any;
  currentCellIsFlagged?: any;
  onFlag?: () => void;
};

export const TransactionTableEmpty: React.FC<Props> = ({
  cellLabel = 'No transactions',
  selectedTransaction,
  currentCellIsFlagged,
  onFlag,
}) => {
  const { isConsolidated, allowFlagging } = useTransactionsTableContext();

  const accountId = selectedTransaction?.id?.split('_')[0] || '';
  const { dismissedAudit } = useNTContext();
  const auditResult = selectedTransaction?.auditResult;

  const auditTypeResult = useMemo(() => {
    const isDismissedLocally = dismissedAudit.some(da => da.rowIndex === Number(accountId) && da.isDismissed === true);
    if (isDismissedLocally) return 'dismissed';
    return auditResult?.type?.toLowerCase();
  }, [auditResult?.type, dismissedAudit, accountId]);

  const isFlaggingDisabled = !allowFlagging;
  const ApiStatusToUIStatusMap: Record<string, 'open' | 'inProgress' | 'closed'> = {
    Open: 'open',
    'In Progress': 'inProgress',
    Closed: 'closed',
  };

  // @ts-ignore
  const auditResultStateShown = ['warning', 'error', 'fail', 'dismissed'];
  const showAuditAlert = auditResult && auditResultStateShown.includes(auditResult.type?.toLowerCase());

  const getAuditContainerClass = () => {
    const baseClass = 'transactions-table-empty-audit';
    if (!showAuditAlert) return baseClass;

    if (auditTypeResult === 'dismissed') {
      return `${baseClass} dismissed`;
    }
    if (auditTypeResult === 'error' || auditTypeResult === 'fail') {
      return `${baseClass} ${baseClass}--error`;
    }
    return baseClass;
  };

  return (
    <div className="transactions-table-empty">
      <div className="transactions-table-empty-flag-button">
        {isFlaggingDisabled ? (
          <Whisper
            placement="top"
            speaker={<Tooltip>{getFlaggingTooltipMessage(allowFlagging!, isConsolidated!)}</Tooltip>}
          >
            <div>
              <KSButton
                onClick={undefined}
                size="medium"
                label={
                  currentCellIsFlagged ? (
                    <>
                      <IconFlagFilled /> Review
                    </>
                  ) : (
                    <>
                      <IconFlag /> Flag Cell
                    </>
                  )
                }
                color={
                  currentCellIsFlagged
                    ? ApiStatusToUIStatusMap[currentCellIsFlagged.status as keyof typeof ApiStatusToUIStatusMap]
                    : 'secondary'
                }
                disabled={isFlaggingDisabled}
              />
            </div>
          </Whisper>
        ) : (
          <KSButton
            onClick={onFlag}
            size="medium"
            label={
              currentCellIsFlagged ? (
                <>
                  <IconFlagFilled /> Review
                </>
              ) : (
                <>
                  <IconFlag /> Flag Cell
                </>
              )
            }
            color={
              currentCellIsFlagged
                ? ApiStatusToUIStatusMap[currentCellIsFlagged.status as keyof typeof ApiStatusToUIStatusMap]
                : 'secondary'
            }
          />
        )}
      </div>

      <div className="transactions-table-empty-header">
        <h3>{cellLabel || 'Vendor'}</h3>
        <p>has no transaction data for {selectedTransaction?.date || 'the selected period'}</p>
      </div>

      {showAuditAlert && (
        <div className={getAuditContainerClass()}>
          <AuditAlert
            auditResult={auditResult}
            accountId={accountId}
            className={'audit-alert'}
            iconClassName={classNames('audit-alert-icon', {
              'audit-alert-icon-dismissed': auditTypeResult === 'dismissed',
            })}
            textClassName="audit-alert-text"
            titleClassName="audit-alert-title"
            messageClassName="audit-alert-message"
            titleIconClassName="audit-alert-title-icon"
          />
        </div>
      )}
    </div>
  );
};

export default TransactionTableEmpty;
